import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthProvider';
import { useBriefingZones } from '../../hooks/useBriefingZones';
import { useNowcastZones } from '../../hooks/useNowcastZones';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';

/**
 * Auth control for the Topbar. Logged out: Entrar. Logged in: "A minha área",
 * a grant-filtered menu of the user's modules (desktop: dropdown; mobile: flat
 * section) plus Conta/Sair. As everywhere in this app the filtering is a
 * rendering decision, not a security boundary: every target page self-gates
 * and the RPCs/RLS are the real gate.
 */
export default function AuthNav({
  isLanding,
  onNavigate,
  variant = 'desktop',
}: {
  isLanding: boolean;
  onNavigate?: () => void;
  variant?: 'desktop' | 'mobile';
}) {
  const { user, signOut, configured } = useAuth();
  const vip = useVipLinks(Boolean(user));
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!configured) return null;

  const cls = isLanding
    ? 'px-3 py-1.5 rounded-lg text-sm font-medium text-mist-muted hover:text-mist hover:bg-ink-800 transition-colors'
    : 'px-3 py-1.5 rounded-lg text-sm font-medium text-tutela-slate hover:text-tutela-forest hover:bg-tutela-forest/5 transition-colors';

  if (!user) {
    return (
      <NavLink to="/entrar" onClick={onNavigate} className={cls}>
        Entrar
      </NavLink>
    );
  }

  const itemCls = `block w-full text-left ${cls}`;
  const navigate = () => {
    setOpen(false);
    onNavigate?.();
  };
  const accountItems = (
    <>
      <NavLink to="/conta" onClick={navigate} className={itemCls}>
        Conta
      </NavLink>
      <button
        type="button"
        onClick={() => {
          void signOut();
          navigate();
        }}
        className={itemCls}
      >
        Sair
      </button>
    </>
  );

  if (variant === 'mobile') {
    return (
      <div className="flex flex-col gap-1">
        {vip.length > 0 && (
          <>
            <div
              className={`px-3 pb-1 font-mono text-xs uppercase tracking-widest ${
                isLanding ? 'text-mist-faint' : 'text-tutela-slate/70'
              }`}
            >
              A minha área
            </div>
            {vip.map((l) => (
              <NavLink key={l.to} to={l.to} onClick={navigate} className={itemCls}>
                {l.label}
              </NavLink>
            ))}
            <div className={`my-1 border-t ${isLanding ? 'border-ink-800' : 'border-gray-100'}`} />
          </>
        )}
        {accountItems}
      </div>
    );
  }

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${cls} inline-flex items-center gap-1`}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        A minha área
        <svg
          className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          className={`absolute right-0 top-full z-50 mt-2 min-w-48 rounded-xl border p-1.5 shadow-lg ${
            isLanding ? 'border-ink-700 bg-ink-900' : 'border-gray-100 bg-white'
          }`}
        >
          {vip.map((l) => (
            <NavLink key={l.to} to={l.to} onClick={navigate} className={itemCls}>
              {l.label}
            </NavLink>
          ))}
          {vip.length > 0 && (
            <div className={`my-1 border-t ${isLanding ? 'border-ink-800' : 'border-gray-100'}`} />
          )}
          {accountItems}
        </div>
      )}
    </div>
  );
}

/** Grant-filtered module links for the logged-in menu. Empty while loading or
 * when the user holds no grants; each condition mirrors the target page's own
 * gate so the menu never links to a "acesso restrito" wall. */
function useVipLinks(loggedIn: boolean): { to: string; label: string }[] {
  const { zones: briefing, loading: bLoading } = useBriefingZones();
  const { zones: nowcast, loading: nLoading } = useNowcastZones();
  const { access: isAdmin, loading: aLoading } = useFeatureAccess('nowcast_admin');
  const { access: simulation, loading: sLoading } = useFeatureAccess('simulation');

  if (!loggedIn || bLoading || nLoading || aLoading || sLoading) return [];

  const union = Array.from(new Set([...(briefing ?? []), ...(nowcast ?? [])])).sort();
  const links: { to: string; label: string }[] = [];
  if (union.length > 0) links.push({ to: `/ignis/municipio/${union[0]}`, label: 'Município' });
  if ((briefing ?? []).length > 0) links.push({ to: '/ignis/briefing', label: 'Briefing' });
  if ((nowcast ?? []).length > 0 || isAdmin) links.push({ to: '/ignis/nowcast', label: 'Nowcast' });
  if (simulation) links.push({ to: '/ignis/simulation', label: 'Simulação' });
  if (union.length > 0) {
    // Limes has per-municipality artifacts and VFX is the only one baked so
    // far; a multi-zone user (admin) must not land on a concelho with no
    // console. Revisit when municipality #2 ships.
    const limesSlug = union.includes('vila-franca-de-xira') ? 'vila-franca-de-xira' : union[0];
    links.push({ to: `/limes/${limesSlug}`, label: 'Faixas (Limes)' });
  }
  return links;
}
